// src/components/TrendingSection.jsx
// ─────────────────────────────────────────────────────────────────
//  Premium TrendingSection — 24News design system
//  MOBILE FIX: Swiper overflow:visible was expanding the document
//  width on phones, causing a blank right half and breaking all
//  ancestor layouts. Fixed with a dedicated clip wrapper that gives
//  Swiper a contained overflow:hidden parent while still allowing
//  card box-shadows via inset padding.
// ─────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import PostMeta from "./PostMeta";
import { useTranslation } from "../i18n";
import { withLangPrefix } from "../lib/i18nRoutes";
import api from "../lib/api";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Keyboard, A11y, Autoplay } from 'swiper/modules';
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

/* ═══════════════════════════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════════════════════════ */
const FALLBACK         = (w, h) => `https://picsum.photos/${w}/${h}`;
const STORAGE_LANG_KEY = process.env.REACT_APP_LANG_KEY || "site_lang";
const DEFAULT_LANG     = "en";
const POLL_MS          = 60_000;

/* ═══════════════════════════════════════════════════════════════
   HELPERS (unchanged from original)
═══════════════════════════════════════════════════════════════ */
function readStoredLang() {
  try { const v = localStorage.getItem(STORAGE_LANG_KEY); if (v) return v; } catch {}
  return (navigator.language || DEFAULT_LANG).split("-")[0];
}

function isAbort(err) {
  if (!err) return false;
  return err.name === "CanceledError" || err.name === "AbortError" || err.__CANCEL__ === true;
}

function getTranslatedField(item, field, lang) {
  if (!item) return null;
  const top = item[field];
  if (top !== undefined && top !== null && String(top).trim() !== "") return top;
  if (item.translations?.[lang]?.[field]) return item.translations[lang][field];
  if (item.translated?.[lang]?.[field])   return item.translated[lang][field];
  if (item[`${field}_${lang}`])           return item[`${field}_${lang}`];
  if (item.i18n?.[lang]?.[field])         return item.i18n[lang][field];
  if (item.trans?.[lang]?.[field])        return item.trans[lang][field];
  return item[field] ?? null;
}

/* ═══════════════════════════════════════════════════════════════
   TRENDING CARD
═══════════════════════════════════════════════════════════════ */
const TrendingCard = React.memo(({ item, lang, index, isRTL }) => {
  if (!item) return null;

  const link     = item.href || withLangPrefix(`/articles/${item.slug ?? item.id}`, lang);
  const title    = getTranslatedField(item, "title", lang) || item.title || "Untitled";
  const image    = item.image_url || item.image || item.thumbnail || FALLBACK(640, 480);
  const date     = item.published_at || item.created_at || null;
  const category = item.section || item.category || null;

  return (
    <Link
      to={link}
      className="tc-card"
      aria-label={title}
      style={{ animationDelay: `${index * 55}ms` }}
    >
      {/* ── Image ── */}
      <div className="tc-img-wrap">
        <img
          src={image} alt={title}
          loading="lazy" decoding="async"
          className="tc-img"
          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = FALLBACK(640, 480); }}
        />
        <div className="tc-overlay" aria-hidden />

        {category && (
          <div className="tc-cat">
            <span className="tc-cat-dot" aria-hidden />
            {category}
          </div>
        )}

        <div className="tc-num" aria-label={`Rank ${index + 1}`}>
          #{String(index + 1).padStart(2, "0")}
        </div>

        {/* Ghost large number */}
        <div className="tc-ghost-num" aria-hidden>
          {String(index + 1).padStart(2, "0")}
        </div>
      </div>

      {/* ── Body ── */}
      <div className="tc-body">
        <div className="tc-accent" aria-hidden />
        <div className="tc-body-inner">
          <h3 className="tc-title">{title}</h3>
          <div className="tc-foot">
            <PostMeta date={date} className="tc-meta" />
            <span className={`tc-arrow${isRTL ? " tc-arrow--rtl" : ""}`} aria-hidden>
              <i className="bi bi-arrow-right" />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
});

/* ═══════════════════════════════════════════════════════════════
   SKELETON
═══════════════════════════════════════════════════════════════ */
const TrendingSkeleton = ({ index = 0 }) => (
  <div className="tc-skeleton" style={{ animationDelay: `${index * 70}ms` }}>
    <div className="tc-sk-img" />
    <div className="tc-sk-body">
      <div className="tc-sk-line tc-sk-line--lg" />
      <div className="tc-sk-line tc-sk-line--md" />
      <div className="tc-sk-line tc-sk-line--sm" />
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */
const TrendingSection = ({ articles = [], title, max = 10 }) => {
  const { lang, t, setLang } = useTranslation();
  const isRTL = ["ar", "he", "fa", "ur"].includes(lang);

  const [trending, setTrending] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [mounted,  setMounted]  = useState(false);

  const swiperRef = useRef(null);

  useEffect(() => {
    const id = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(id);
  }, []);

  const itemsFromProps = useMemo(
    () => (Array.isArray(articles) ? articles.slice(0, max) : []),
    [articles, max]
  );

  const displayItems = trending.length ? trending.slice(0, max) : itemsFromProps;
  const hasItems     = displayItems.length > 0;

  /* ── Language listeners ── */
  useEffect(() => {
    const onStorage  = (e) => { if (e?.key === STORAGE_LANG_KEY) setLang(e.newValue || readStoredLang()); };
    const onSiteLang = (e) => { setLang(e?.detail?.lang || readStoredLang()); };
    window.addEventListener("storage",               onStorage);
    window.addEventListener("site:language_changed", onSiteLang);
    window.addEventListener("site:lang_changed",     onSiteLang);
    return () => {
      window.removeEventListener("storage",               onStorage);
      window.removeEventListener("site:language_changed", onSiteLang);
      window.removeEventListener("site:lang_changed",     onSiteLang);
    };
  }, [setLang]);

  /* ── Fetch + poll ── */
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const fetchData = async () => {
      try {
        setError(null);
        const res = await api.get("/articles/trending/", {
          params: { limit: max || 12 },
          signal: controller.signal,
        });
        if (cancelled) return;
        const mapped = (res.data.results || res.data || []).map((a) => ({
          ...a,
          href: withLangPrefix(
            a.url?.startsWith("/articles/") ? a.url : `/articles/${encodeURIComponent(a.slug ?? "")}`,
            lang
          ),
          image_url: a.image_url ?? a.image ?? a.thumbnail ?? null,
        }));
        setTrending(mapped);
      } catch (err) {
        if (isAbort(err) || controller.signal.aborted) return;
        console.error("Trending fetch error:", err);
        setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, POLL_MS);
    return () => { cancelled = true; controller.abort(); clearInterval(interval); };
  }, [lang, max]);

  /* ── Swiper config ── */
  // Detect mobile once — window.innerWidth < 900 covers all phones & small tablets.
  // We disable autoplay on touch devices: users are already swiping manually,
  // and auto-advancing while someone is reading is jarring on small screens.
  const isMobile = typeof window !== "undefined" && window.innerWidth < 900;

  const swiperConfig = useMemo(() => ({
    modules:       [Navigation, Pagination, Keyboard, A11y, Autoplay],
    spaceBetween:  16,
    loop:          hasItems && displayItems.length >= 3,
    loopedSlides:  Math.max(3, displayItems.length),
    watchOverflow: true,
    grabCursor:    true,
    dir:           isRTL ? "rtl" : "ltr",
    keyboard:      { enabled: true, onlyInViewport: true },
    speed:         500,
    // autoplay: off on mobile, gentle 5s on desktop
    autoplay: isMobile
      ? false
      : { delay: 5000, disableOnInteraction: true, pauseOnMouseEnter: true },
    navigation: {
      prevEl:          ".ts-nav-prev",
      nextEl:          ".ts-nav-next",
      disabledClass:   "ts-nav--off",
    },
    pagination: {
      el:                 ".ts-dots",
      clickable:          true,
      bulletClass:        "ts-dot",
      bulletActiveClass:  "ts-dot--on",
      dynamicBullets:     true,
      dynamicMainBullets: 3,
    },
    breakpoints: {
      0:    { slidesPerView: 2,   spaceBetween: 10 },
      580:  { slidesPerView: 2,   spaceBetween: 14 },
      900:  { slidesPerView: 3,   spaceBetween: 16 },
      1280: { slidesPerView: 4,   spaceBetween: 18 },
    },
  }), [hasItems, displayItems.length, isRTL, isMobile]);

  /* ─── RENDER ─── */
  const heading = title || t("trending_now") || "Trending Now";

  return (
    <section
      className={`ts-root${mounted ? " ts-in" : ""}${isRTL ? " ts-rtl" : ""}`}
      aria-label={heading}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <TrendingStyles />

      {/* ════════ HEADER ════════ */}
      <div className="ts-head">
        <div className="ts-head-l">
          <span className="ts-pulse" aria-hidden />
          <div className="ts-badge">
            <i className="bi bi-lightning-charge-fill" aria-hidden />
            {heading}
          </div>
          <div className="ts-sep" aria-hidden />
          {hasItems && (
            <span className="ts-count">
              {displayItems.length} {t("stories") || "stories"}
            </span>
          )}
        </div>

        <div className="ts-rule" aria-hidden />

        {/* Desktop nav arrows — in header row */}
        {hasItems && displayItems.length >= 3 && (
          <div className="ts-head-nav" aria-label="Carousel navigation">
            <button className="ts-nav ts-nav-prev" aria-label={t("previous") || "Previous"}>
              <i className={`bi ${isRTL ? "bi-chevron-right" : "bi-chevron-left"}`} />
            </button>
            <button className="ts-nav ts-nav-next" aria-label={t("next") || "Next"}>
              <i className={`bi ${isRTL ? "bi-chevron-left" : "bi-chevron-right"}`} />
            </button>
          </div>
        )}
      </div>

      {/* ════════ SKELETON ════════ */}
      {loading && !hasItems && (
        <div className="ts-sk-grid">
          {Array.from({ length: 4 }).map((_, i) => <TrendingSkeleton key={i} index={i} />)}
        </div>
      )}

      {/* ════════ ERROR ════════ */}
      {error && !hasItems && (
        <div className="ts-error" role="alert">
          <i className="bi bi-exclamation-circle" aria-hidden />
          {t("load_error") || "Could not load trending stories."}
        </div>
      )}

      {/* ════════ CAROUSEL ════════
           KEY FIX: .ts-clip has overflow:hidden so Swiper's internal
           overflow never expands the document width on mobile.
           Padding inside .ts-clip lets card box-shadows render without
           being clipped at the component edges.
      ════════════════════════════ */}
      {hasItems && (
        <div className="ts-carousel">
          <div className="ts-clip">
            <Swiper {...swiperConfig} ref={swiperRef} className="ts-swiper">
              {displayItems.map((item, idx) => (
                <SwiperSlide key={item.id ?? item.slug ?? idx} className="ts-slide">
                  <TrendingCard item={item} lang={lang} index={idx} isRTL={isRTL} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {displayItems.length >= 3 && (
            <div className="ts-dots" aria-label="Carousel pages" />
          )}
        </div>
      )}
    </section>
  );
};

/* ═══════════════════════════════════════════════════════════════
   STYLE INJECTION — once per session
═══════════════════════════════════════════════════════════════ */
let _tsCSS = false;
function TrendingStyles() {
  useEffect(() => {
    if (_tsCSS || document.getElementById("ts-css")) return;
    _tsCSS = true;
    const el = document.createElement("style");
    el.id = "ts-css";
    el.textContent = CSS;
    document.head.appendChild(el);
  }, []);
  return null;
}

/* ═══════════════════════════════════════════════════════════════
   CSS
═══════════════════════════════════════════════════════════════ */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=Cormorant+Garamond:wght@600;700;800&family=Noto+Sans+Arabic:wght@400;500;600;700&display=swap');

/* ── Design tokens (mirrors header.jsx exactly) ── */
:root {
  --nh-font:   'Sora','Helvetica Neue',sans-serif;
  --nh-font-ar:'Noto Sans Arabic','Arabic UI Text',Tahoma,sans-serif;
  --nh-serif:  'Cormorant Garamond','Georgia',serif;
  --nh-red:    #e63311;
  --nh-red-dk: #c4280d;
  --nh-ink:    #111;
  --nh-ink2:   #6b7280;
  --nh-bg:     #fff;
  --nh-bg2:    #f7f7f7;
  --nh-border: rgba(0,0,0,.08);
  --nh-sh-sm:  0 1px 4px rgba(0,0,0,.06);
  --nh-sh-md:  0 8px 24px rgba(0,0,0,.10);
  --nh-r:      10px;
  --nh-t:      0.18s cubic-bezier(.4,0,.2,1);
}

/* ══════════════════════════════════════════════
   ROOT
   max-width:100% + overflow-x:hidden prevents
   ANY child from expanding the page horizontally
══════════════════════════════════════════════ */
.ts-root, .ts-root * { box-sizing: border-box; }
.ts-root {
  font-family: var(--nh-font);
  color: var(--nh-ink);
  width: 100%;
  max-width: 100%;
  /* 🔑 This (and ts-clip below) are the two guards that stop
     Swiper from expanding the document width on mobile. */
  overflow-x: hidden;
  padding: clamp(20px, 3.5vw, 44px) 0;
  position: relative;
}
.ts-rtl, .ts-rtl * { font-family: var(--nh-font-ar); }

/* mount animation */
.ts-root .ts-head,
.ts-root .ts-carousel {
  opacity: 0;
  transform: translateY(10px);
  transition: opacity .4s ease, transform .4s ease;
}
.ts-root.ts-in .ts-head    { opacity: 1; transform: none; transition-delay: 0ms; }
.ts-root.ts-in .ts-carousel{ opacity: 1; transform: none; transition-delay: 70ms; }

/* ══════════════════════════════════════════════
   HEADER ROW
══════════════════════════════════════════════ */
.ts-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: clamp(14px, 2.5vw, 22px);
  /* Never let header overflow either */
  max-width: 100%;
  overflow: hidden;
}
.ts-head-l {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  min-width: 0;
}

/* Pulsing dot */
.ts-pulse {
  width: 7px; height: 7px;
  border-radius: 50%;
  background: var(--nh-red);
  flex-shrink: 0;
  animation: ts-pulse 1.8s ease-in-out infinite;
}
@keyframes ts-pulse {
  0%,100% { box-shadow: 0 0 0 0 rgba(230,51,17,.5); }
  50%      { box-shadow: 0 0 0 6px rgba(230,51,17,0); }
}

/* Badge */
.ts-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: rgba(230,51,17,.08);
  color: var(--nh-red);
  border: 1px solid rgba(230,51,17,.18);
  font-size: .6rem;
  font-weight: 700;
  letter-spacing: .09em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 3px;
  white-space: nowrap;
}
.ts-badge .bi { font-size: .7rem; }

.ts-sep {
  width: 1px; height: 16px;
  background: var(--nh-border);
  flex-shrink: 0;
}
.ts-count {
  font-size: .7rem;
  font-weight: 600;
  color: var(--nh-ink2);
  white-space: nowrap;
}

/* Rule */
.ts-rule {
  flex: 1;
  height: 1px;
  min-width: 0;
  background: linear-gradient(to right, var(--nh-red) 28px, var(--nh-border) 28px);
}
.ts-rtl .ts-rule {
  background: linear-gradient(to left, var(--nh-red) 28px, var(--nh-border) 28px);
}

/* Desktop nav arrows (hidden on mobile via responsive) */
.ts-head-nav { display: flex; gap: 5px; flex-shrink: 0; }

/* ══════════════════════════════════════════════
   NAV BUTTONS
══════════════════════════════════════════════ */
.ts-nav {
  width: 34px; height: 34px;
  border-radius: 8px;
  border: 1.5px solid var(--nh-border);
  background: var(--nh-bg2);
  color: var(--nh-ink2);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  font-size: .875rem;
  transition: border-color var(--nh-t), background var(--nh-t),
              color var(--nh-t), transform .1s;
}
.ts-nav:hover:not(.ts-nav--off) {
  border-color: var(--nh-red);
  background: #fef2f2;
  color: var(--nh-red);
}
.ts-nav:active:not(.ts-nav--off) { transform: scale(.93); }
.ts-nav--off { opacity: .35; cursor: not-allowed; }
.ts-nav:focus-visible {
  outline: 2px solid var(--nh-red);
  outline-offset: 2px;
}

/* ══════════════════════════════════════════════
   CAROUSEL
   .ts-clip is the critical overflow guard:
   - overflow:hidden clips Swiper within the component bounds
   - padding allows box-shadows on cards to render without being cut
   - negative margin compensates for the padding so cards stay aligned
══════════════════════════════════════════════ */
.ts-carousel {
  position: relative;
  padding-bottom: 28px; /* room for pagination dots */
}

.ts-clip {
  overflow: hidden;            /* 🔑 stops Swiper bleeding outside */
  /* padding lets card lift shadows show at edges without being clipped */
  padding: 4px 2px 8px;
  margin: -4px -2px -8px;
}

/* Swiper: DO NOT set overflow:visible here — that's what broke mobile.
   Let Swiper use its default overflow (hidden) and rely on ts-clip padding. */
.ts-swiper { width: 100%; }
.ts-slide   { height: auto; display: flex; }

/* ══════════════════════════════════════════════
   PAGINATION DOTS
══════════════════════════════════════════════ */
.ts-dots {
  position: absolute;
  bottom: 0; left: 0; right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
  z-index: 10;
}
.ts-dot {
  width: 5px; height: 5px;
  border-radius: 999px;
  background: var(--nh-border);
  cursor: pointer;
  border: none;
  transition: width var(--nh-t), background var(--nh-t), box-shadow var(--nh-t);
  outline: none;
}
.ts-dot--on {
  width: 22px;
  background: var(--nh-red);
  box-shadow: 0 2px 8px rgba(230,51,17,.35);
}
.ts-dot:focus-visible { outline: 2px solid var(--nh-red); outline-offset: 2px; }

/* ══════════════════════════════════════════════
   CARD
══════════════════════════════════════════════ */
.tc-card {
  display: flex;
  flex-direction: column;
  width: 100%; height: 100%;
  text-decoration: none;
  color: inherit;
  background: var(--nh-bg);
  border: 1px solid var(--nh-border);
  border-radius: var(--nh-r);
  overflow: hidden;
  box-shadow: var(--nh-sh-sm);
  /* Use will-change so the GPU handles the lift — avoids layout thrash */
  will-change: transform, box-shadow;
  transition:
    border-color var(--nh-t),
    box-shadow   var(--nh-t),
    transform   .18s cubic-bezier(.4,0,.2,1);
  animation: tc-in .5s ease both;
}
@keyframes tc-in {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: none; }
}
.tc-card:hover {
  border-color: rgba(230,51,17,.3);
  box-shadow: var(--nh-sh-md);
  transform: translateY(-3px);
}
.tc-card:focus-visible {
  outline: 2px solid var(--nh-red);
  outline-offset: 2px;
}

/* Image */
.tc-img-wrap {
  position: relative;
  width: 100%;
  aspect-ratio: 16/10;
  overflow: hidden;
  background: var(--nh-bg2);
  flex-shrink: 0;
}
.tc-img {
  width: 100%; height: 100%;
  object-fit: cover;
  display: block;
  transition: transform .55s cubic-bezier(.4,0,.2,1);
}
.tc-card:hover .tc-img { transform: scale(1.06); }

.tc-overlay {
  position: absolute; inset: 0;
  background: linear-gradient(to bottom, rgba(0,0,0,0) 40%, rgba(0,0,0,.46) 100%);
  pointer-events: none;
}

/* Category */
.tc-cat {
  position: absolute; top: 8px; left: 8px;
  display: inline-flex; align-items: center; gap: 4px;
  background: rgba(0,0,0,.72);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: #fff;
  border: 1px solid rgba(255,255,255,.12);
  font-size: .58rem; font-weight: 700;
  letter-spacing: .07em; text-transform: uppercase;
  padding: 4px 9px; border-radius: 3px;
}
.tc-cat-dot {
  width: 3px; height: 3px;
  border-radius: 50%;
  background: var(--nh-red);
  flex-shrink: 0;
}

/* Red rank chip — top right */
.tc-num {
  position: absolute; top: 8px; right: 8px;
  background: var(--nh-red);
  color: #fff;
  font-size: .58rem; font-weight: 800;
  padding: 3px 8px; border-radius: 3px;
  letter-spacing: .04em;
}

/* Ghost number */
.tc-ghost-num {
  position: absolute; bottom: 4px; right: 8px;
  font-size: 3rem; font-weight: 900;
  font-family: var(--nh-serif);
  color: rgba(255,255,255,.1);
  line-height: 1;
  user-select: none; pointer-events: none;
  letter-spacing: -.04em;
}

/* Body */
.tc-body {
  display: flex;
  align-items: stretch;
  flex: 1;
  background: var(--nh-bg);
}
.tc-accent {
  width: 3px; flex-shrink: 0;
  background: var(--nh-border);
  transition: background var(--nh-t);
}
.tc-card:hover .tc-accent { background: var(--nh-red); }

.tc-body-inner {
  padding: 11px 12px 10px;
  display: flex; flex-direction: column;
  gap: 8px; flex: 1; min-width: 0;
}

/* Title */
.tc-title {
  margin: 0;
  font-family: var(--nh-serif);
  font-size: clamp(.875rem, 1.2vw, 1.0625rem);
  font-weight: 700;
  line-height: 1.38;
  letter-spacing: -.01em;
  color: var(--nh-ink);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: color var(--nh-t);
}
.ts-rtl .tc-title {
  font-family: var(--nh-font-ar);
  letter-spacing: 0;
  line-height: 1.55;
}
.tc-card:hover .tc-title { color: var(--nh-red); }

/* Footer */
.tc-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: auto;
  padding-top: 7px;
  border-top: 1px solid var(--nh-border);
}
.tc-meta { font-size: .68rem; color: var(--nh-ink2); }

/* Arrow */
.tc-arrow {
  width: 26px; height: 26px;
  border-radius: 6px; flex-shrink: 0;
  border: 1px solid var(--nh-border);
  background: var(--nh-bg2);
  color: var(--nh-ink2);
  display: flex; align-items: center; justify-content: center;
  font-size: .75rem;
  transition: border-color var(--nh-t), background var(--nh-t),
              color var(--nh-t), transform var(--nh-t);
}
.tc-arrow--rtl { transform: scaleX(-1); }
.tc-card:hover .tc-arrow {
  border-color: var(--nh-red);
  background: var(--nh-red);
  color: #fff;
  transform: translateX(2px);
}
.tc-card:hover .tc-arrow--rtl { transform: scaleX(-1) translateX(2px); }

/* ══════════════════════════════════════════════
   SKELETON
══════════════════════════════════════════════ */
.ts-sk-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 14px;
}
.tc-skeleton {
  background: var(--nh-bg);
  border: 1px solid var(--nh-border);
  border-radius: var(--nh-r);
  overflow: hidden;
  animation: tc-in .4s ease both;
}
@keyframes ts-sk {
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
}
.tc-sk-img {
  aspect-ratio: 16/10;
  background: linear-gradient(90deg, #ebebeb 25%, #f5f5f5 50%, #ebebeb 75%);
  background-size: 1200px 100%;
  animation: ts-sk 1.6s ease-in-out infinite;
}
.tc-sk-body { padding: 11px; display: flex; flex-direction: column; gap: 8px; }
.tc-sk-line {
  border-radius: 4px;
  background: linear-gradient(90deg, #ebebeb 25%, #f5f5f5 50%, #ebebeb 75%);
  background-size: 1200px 100%;
  animation: ts-sk 1.6s ease-in-out infinite;
  height: 11px;
}
.tc-sk-line--lg { width: 90%; }
.tc-sk-line--md { width: 70%; animation-delay: .08s; }
.tc-sk-line--sm { width: 45%; animation-delay: .16s; }

/* ══════════════════════════════════════════════
   ERROR
══════════════════════════════════════════════ */
.ts-error {
  display: flex; align-items: center; gap: 8px;
  padding: 16px 14px;
  border-radius: var(--nh-r);
  border: 1px solid rgba(230,51,17,.18);
  background: rgba(230,51,17,.04);
  color: var(--nh-red);
  font-size: .8125rem; font-weight: 600;
}
.ts-error .bi { font-size: 1rem; flex-shrink: 0; }

/* ══════════════════════════════════════════════
   RESPONSIVE
══════════════════════════════════════════════ */

/* ── Mobile: ≤ 580px ── */
@media (max-width: 580px) {
  /* Hide desktop nav arrows — swipe gesture is the interaction */
  .ts-head-nav { display: none; }

  .ts-badge { font-size: .56rem; padding: 3px 8px; }
  .ts-count  { display: none; }        /* reduces header clutter */
  .ts-sep    { display: none; }

  .ts-carousel { padding-bottom: 24px; }

  /* Dots smaller on mobile */
  .ts-dot     { width: 4px; height: 4px; }
  .ts-dot--on { width: 14px; }

  /* Card — 1.65 cards visible on phone, everything scales down tightly */
  .tc-img-wrap    { aspect-ratio: 4/3; }
  .tc-title       { font-size: .78rem; -webkit-line-clamp: 2; }
  .tc-ghost-num   { font-size: 1.75rem; }
  .tc-body-inner  { padding: 8px 9px 8px; gap: 5px; }
  .tc-foot        { padding-top: 5px; }
  .tc-meta        { font-size: .58rem; }
  .tc-arrow       { width: 20px; height: 20px; font-size: .6rem; }
  .tc-cat         { font-size: .52rem; padding: 2px 6px; top: 6px; left: 6px; }
  .tc-num         { font-size: .52rem; padding: 2px 6px; top: 6px; right: 6px; }
  .tc-accent      { width: 2px; }

  /* Skeleton grid → single column */
  .ts-sk-grid { grid-template-columns: 1fr 1fr; gap: 10px; }
}

/* ── Very small phones: ≤ 380px ── */
@media (max-width: 380px) {
  .ts-sk-grid { grid-template-columns: 1fr; }
  .tc-title   { font-size: .8125rem; }
}

/* ── Tablet: 581–899px ── */
@media (min-width: 581px) and (max-width: 899px) {
  .ts-head-nav { display: none; }
  .ts-dots     { display: flex; }
}

/* ── Desktop: ≥ 900px ── */
@media (min-width: 900px) {
  /* Dots less necessary when arrows are visible */
  .ts-dots { opacity: .7; }
}

/* ══════════════════════════════════════════════
   DARK MODE
══════════════════════════════════════════════ */
@media (prefers-color-scheme: dark) {
  :root {
    --nh-ink:    #f1f1f1;
    --nh-ink2:   #9ca3af;
    --nh-bg:     #111;
    --nh-bg2:    #1a1a1a;
    --nh-border: rgba(255,255,255,.09);
  }
  .tc-sk-img, .tc-sk-line {
    background: linear-gradient(90deg, #1e1e1c 25%, #2a2a28 50%, #1e1e1c 75%);
    background-size: 1200px 100%;
  }
  .tc-arrow  { background: #1a1a1a; }
  .ts-nav    { background: #1a1a1a; }
  .ts-nav:hover:not(.ts-nav--off) { background: #1e0f0f; }
  .ts-dot    { background: rgba(255,255,255,.18); }
  .ts-badge  { background: rgba(230,51,17,.12); }
}

/* ══════════════════════════════════════════════
   REDUCED MOTION
══════════════════════════════════════════════ */
@media (prefers-reduced-motion: reduce) {
  .tc-card, .tc-img, .tc-arrow, .ts-dot, .ts-nav {
    animation: none !important;
    transition: none !important;
  }
  .ts-pulse { animation: none !important; box-shadow: none !important; }
}

/* ══════════════════════════════════════════════
   PRINT
══════════════════════════════════════════════ */
@media print {
  .ts-carousel  { padding-bottom: 0; }
  .ts-dots, .ts-head-nav { display: none; }
}
`;

export default TrendingSection;